import React, { useState, useEffect, useCallback, useRef } from 'react';

import { adminAPI } from '../../services/api';
import ContentLoader from 'react-content-loader';
import { useParams } from "react-router-dom";

const AdminUsers = () => {
  const { isOpenTraining } = useParams();
  const [users, setUsers] = useState([]);
  const [nurse, setNurse] = useState([]);
  const [inProgessNurse, setInProgessNurse] = useState([]);
  const [certiFiedNurse, setCertiFiedNurse] = useState([]);
  const [showNurseTable, setShowNurseTable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false); // Separate loading for table
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });


  // Filter states
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Separate states for metric cards and filter tabs
  const [metricCards, setMetricCards] = useState({
    patient: 0,
    psychiatrist: 0,
    nurse: 0,
    admin: 0
  });

  const [filterCounts, setFilterCounts] = useState({
    all: 0,
    patient: 0,
    psychiatrist: 0,
    nurse: 0,
    admin: 0,
    suspended: 0
  });

  // Nurse profile modal state
  const [showNurseProfileModal, setShowNurseProfileModal] = useState(false);
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [nurseProfileLoading, setNurseProfileLoading] = useState(false);
  const [nurseProfileData, setNurseProfileData] = useState(null);

  // Ref for debounce timeout
  const searchTimeoutRef = useRef(null);

  // Fetch all role counts for metric cards (always shows total)
  const fetchMetricCounts = async () => {
    try {
      const [allRes, patientRes, doctorRes, nurseRes, adminRes] = await Promise.all([
        adminAPI.getAllUsers(1, 1),
        adminAPI.filterUsers({ role: 'patient', limit: 1 }),
        adminAPI.filterUsers({ role: 'psychiatrist', limit: 1 }),
        adminAPI.filterUsers({ role: 'nurse', limit: 1 }),
        adminAPI.filterUsers({ role: 'admin', limit: 1 })
      ]);

      setMetricCards({
        patient: patientRes.pagination.totalUsers,
        psychiatrist: doctorRes.pagination.totalUsers,
        nurse: nurseRes.pagination.totalUsers,
        admin: adminRes.pagination.totalUsers
      });
    } catch (err) {
      console.error('Error fetching metric counts:', err);
    }
  };


  const getUserDisplayName = (user) => {
    if (user.name) return user.name;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    return user.email.split('@')[0]; // fallback to email username
  };

  // Function to fetch nurse profile data
  const fetchNurseProfile = async (nurseId) => {
    try {
      console.log('🔄 Fetching nurse profile for ID:', nurseId);
      setNurseProfileLoading(true);
      const response = await adminAPI.getNurseProfile(nurseId);
      console.log('📦 API Response:', response);
      if (response.success) {
        console.log('✅ Profile data received:', response.data);
        setNurseProfileData(response.data);
      } else {
        console.error('❌ Failed to fetch nurse profile:', response.error);
      }
    } catch (error) {
      console.error('❌ Error fetching nurse profile:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
    } finally {
      setNurseProfileLoading(false);
    }
  };

  // Function to open nurse profile modal
  const handleViewNurseProfile = (nurse) => {
    setSelectedNurse(nurse);
    setShowNurseProfileModal(true);
    fetchNurseProfile(nurse._id);
  };

  // Fetch users data
  const fetchUsers = async (page = 1, filters = {}, isInitialLoad = false, currentFilterLabel = activeFilter) => {
    try {
      // Only set table loading if it's not the initial load
      if (!isInitialLoad) {
        setTableLoading(true);
      }
      setError(null);

      let response;
      if (Object.keys(filters).length > 0) {
        response = await adminAPI.filterUsers({ ...filters, page, limit: 10 });
      } else {
        response = await adminAPI.getAllUsers(page, 50);
      }

      setUsers(response.users);
      const allNurses = response.users.filter(u => u.role === 'nurse');
      let nurseList = allNurses;

      // Apply nurse sub-filters if active (use the current filter label at time of request)
      if (currentFilterLabel === 'Training in Progress') {
        setShowNurseTable(true);
      } else if (currentFilterLabel === 'Certified Nurses') {
        setShowNurseTable(true);
      } else if (currentFilterLabel === 'Nurses') {
        setShowNurseTable(true);
      }


      const inProgressNurseList = allNurses.filter(u => u.courseInfo && u.courseInfo.isCompleted === false);
      const CertifiedNurseList = allNurses.filter(u => u.courseInfo && u.courseInfo.isCompleted === true);
      
      setInProgessNurse(inProgressNurseList);
      setCertiFiedNurse(CertifiedNurseList);
      setNurse(nurseList);
      setPagination(response.pagination);

      // Update filter counts based on current view
      if (activeFilter === 'All') {
        setFilterCounts({
          all: response.pagination.totalUsers,
          patient: metricCards.patient,
          psychiatrist: metricCards.psychiatrist,
          nurse: metricCards.nurse,
          admin: metricCards.admin,
          suspended: 0
        });
      } else {
        // For specific filter, show the filtered count
        setFilterCounts(prev => ({
          ...prev,
          all: response.pagination.totalUsers,
          [activeFilter.toLowerCase() === 'doctors' ? 'psychiatrist' : activeFilter.toLowerCase().slice(0, -1)]: response.pagination.totalUsers
        }));
      }

    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };


  // Debounced search function with 500ms delay
  const debouncedSearch = useCallback((term) => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(term);
      const filters = {};
      const nurseFilters = ['Nurses', 'Certified Nurses', 'Training in Progress'];

      if (activeFilter !== 'All') {
        if (activeFilter === 'Patients') filters.role = 'patient';
        if (nurseFilters.includes(activeFilter)) filters.role = 'nurse';
        if (activeFilter === 'Admins') filters.role = 'admin';
      }

      if (term) filters.email = term;

      // Keep nurse table visible for nurse filters
      setShowNurseTable(nurseFilters.includes(activeFilter));
      
      fetchUsers(1, filters, false, activeFilter);
    }, 500);
  }, [activeFilter, isOpenTraining]);




  // Handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    const nurseFilter = ['Nurses', 'Certified Nurses', 'Training in Progress'];
    if (nurseFilter.includes(filter)) {
      setShowNurseTable(true);
      setSearchTerm('');
      setDebouncedSearchTerm('');
        if (filter) {
          if (filter === 'Nurses') {
              const filters = {};
              if (filter !== 'All') {
                if (filter === 'Patients') filters.role = 'patient';
                if (filter === 'Admins') filters.role = 'admin';
              }
            fetchUsers(1, filters, false, filter);
          };

          if (filter === 'Certified Nurses' || filter === 'Training in Progress') {
            const isCompleted = filter === 'Certified Nurses';
            const filteredNurses = users.filter(
              u => u.role === 'nurse' && u.courseInfo?.isCompleted === isCompleted
            );
            setNurse(filteredNurses);
          }
        }
      return;
    } else {
      setShowNurseTable(false);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      const filters = {};
      if (filter !== 'All') {
        if (filter === 'Patients') filters.role = 'patient';
        if (filter === 'Admins') filters.role = 'admin';
      }
      fetchUsers(1, filters, false, filter);
    }
  };




  // Initial load
  useEffect(() => {
    const initializeData = async () => {
      await fetchMetricCounts();
      await fetchUsers(1, {}, true);
      if (isOpenTraining) {
        handleFilterChange("Training in Progress");
      }
    };
    initializeData();


  }, [isOpenTraining]);






  // Handle search input change
  const handleSearchChange = (term) => {
    setSearchTerm(term);
    debouncedSearch(term);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    const filters = {};
    if (activeFilter !== 'All') {
      const nurseFilters = ['Nurses', 'Certified Nurses', 'Training in Progress'];
      if (activeFilter === 'Patients') filters.role = 'patient';
      if (nurseFilters.includes(activeFilter)) filters.role = 'nurse';
      if (activeFilter === 'Admins') filters.role = 'admin';
    }
    if (debouncedSearchTerm) filters.email = debouncedSearchTerm;
    fetchUsers(page, filters);
  };



  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);



  // Helper functions
  const formatDateTime = (datetimeStr) => {
    if (!datetimeStr) return 'Never';
    const date = new Date(datetimeStr);
    const options = {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-GB', options).replace(',', ' at');
  };

  const getRoleLabel = (role) => {
    switch (role) {
      // case 'psychiatrist': return 'Doctor';
      case 'patient': return 'Patient';
      case 'nurse': return 'Nurse';
      case 'admin': return 'Admin';
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      // case 'psychiatrist': return 'bg-blue-100 text-blue-800';
      case 'patient': return 'bg-gray-100 text-gray-800';
      case 'nurse': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUserStatus = (user) => {
    if (user.emailVerified) {
      return { status: 'Active', color: 'green' };
    } else {
      return { status: 'Pending', color: 'yellow' };
    }
  };

  const getAvatar = (email) => {
    const hash = email.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    const avatarId = Math.abs(hash) % 50 + 1;
    return `https://i.pravatar.cc/150?img=${avatarId}`;
  };

  // Helper function to get correct image URL (same as in other components)
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return "https://cdn-icons-png.flaticon.com/512/149/149071.png";
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('https')) {
      return imagePath;
    }
    
    // Use the same domain as the API but without /api
    const baseUrl = 'https://backend.adhdrapidcare.com';
    
    // If it's a relative path, add the base URL
    if (imagePath.startsWith('/')) {
      return `${baseUrl}${imagePath}`;
    }
    
    // If it doesn't start with /, add it
    return `${baseUrl}/${imagePath}`;
  };

  // Skeleton Loader Components
  const MetricCardSkeleton = () => (
    <div className="bg-white border border-gray-200 rounded-lg shadow-md p-4 md:p-5">
      <ContentLoader
        speed={2}
        width="100%"
        height={80}
        backgroundColor="#f3f4f6"
        foregroundColor="#e5e7eb"
      >
        <rect x="0" y="0" rx="4" ry="4" width="60" height="16" />
        <rect x="0" y="25" rx="4" ry="4" width="40" height="8" />
        <rect x="0" y="45" rx="4" ry="4" width="80" height="24" />
        <rect x="calc(100% - 50)" y="0" rx="12" ry="12" width="50" height="20" />
      </ContentLoader>
    </div>
  );

  const TableRowSkeleton = () => (
    <div className="rounded-xl border shadow-sm bg-white border-gray-200">
      <ContentLoader
        speed={2}
        width="100%"
        height={80}
        backgroundColor="#f3f4f6"
        foregroundColor="#e5e7eb"
      >
        <rect x="16" y="20" rx="20" ry="20" width="40" height="40" />
        <rect x="70" y="20" rx="4" ry="4" width="120" height="16" />
        <rect x="70" y="40" rx="4" ry="4" width="180" height="12" />
        <rect x="300" y="25" rx="12" ry="12" width="60" height="24" />
        <rect x="400" y="25" rx="12" ry="12" width="60" height="24" />
        <rect x="500" y="25" rx="4" ry="4" width="100" height="16" />
        <rect x="650" y="25" rx="4" ry="4" width="80" height="16" />
      </ContentLoader>
    </div>
  );

  const MobileCardSkeleton = () => (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      <ContentLoader
        speed={2}
        width="100%"
        height={120}
        backgroundColor="#f3f4f6"
        foregroundColor="#e5e7eb"
      >
        <rect x="0" y="0" rx="24" ry="24" width="48" height="48" />
        <rect x="60" y="0" rx="4" ry="4" width="120" height="16" />
        <rect x="60" y="20" rx="4" ry="4" width="180" height="12" />
        <rect x="60" y="40" rx="12" ry="12" width="60" height="24" />
        <rect x="calc(100% - 120)" y="40" rx="4" ry="4" width="120" height="24" />
        <rect x="60" y="70" rx="4" ry="4" width="100" height="12" />
      </ContentLoader>
    </div>
  );

  if (error) {
    return (
   
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => fetchUsers()}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
   
    );
  }

  return (
  <div className="min-h-screen ">
      <div className="mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Manage Users</h1>
        <p className="text-gray-600 mt-2">Add, edit, or review user accounts across your ADHD care platform.</p>
      </div>

      {/* Metric cards - Show skeleton only on initial load */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        {/* Patients */}
        <div className="bg-green-50 border border-gray-200 rounded-lg shadow-md p-4 md:p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Patients</h3>
            {loading ? (
              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full font-medium">
                <ContentLoader
                  speed={2}
                  width={40}
                  height={12}
                  backgroundColor="#f3f4f6"
                  foregroundColor="#e5e7eb"
                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                >
                  <rect x="0" y="0" rx="4" ry="4" width="40" height="12" />
                </ContentLoader>
              </span>
            ) : (
              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full font-medium">
                {(() => {
                  const total = metricCards.patient + metricCards.psychiatrist + metricCards.nurse + metricCards.admin;
                  const percentage = total > 0 ? ((metricCards.patient / total) * 100).toFixed(1) : 0;
                  return `${percentage}%`;
                })()}
              </span>
            )}
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">
            {loading ? (
              <ContentLoader
                speed={2}
                width={60}
                height={28}
                backgroundColor="#f3f4f6"
                foregroundColor="#e5e7eb"
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              >
                <rect x="0" y="0" rx="4" ry="4" width="60" height="28" />
              </ContentLoader>
            ) : (
              metricCards.patient
            )}
          </p>
        </div>

        {/* Doctors */}
        {/* <div className="bg-red-50 border border-gray-200 rounded-lg shadow-md p-4 md:p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Doctors</h3>
            {loading ? (
              <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full font-medium">
                <ContentLoader
                  speed={2}
                  width={40}
                  height={12}
                  backgroundColor="#f3f4f6"
                  foregroundColor="#e5e7eb"
                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                >
                  <rect x="0" y="0" rx="4" ry="4" width="40" height="12" />
                </ContentLoader>
              </span>
            ) : (
              <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full font-medium">
                {(() => {
                  const total = metricCards.patient + metricCards.psychiatrist + metricCards.nurse + metricCards.admin;
                  const percentage = total > 0 ? ((metricCards.psychiatrist / total) * 100).toFixed(1) : 0;
                  return `${percentage}%`;
                })()}
              </span>
            )}
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">
            {loading ? (
              <ContentLoader
                speed={2}
                width={60}
                height={28}
                backgroundColor="#f3f4f6"
                foregroundColor="#e5e7eb"
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              >
                <rect x="0" y="0" rx="4" ry="4" width="60" height="28" />
              </ContentLoader>
            ) : (
              metricCards.psychiatrist
            )}
          </p>
        </div> */}

        {/* Nurses */}
        <div className="bg-purple-50 border border-gray-200 rounded-lg shadow-md p-4 md:p-5 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Nurses</h3>
            {loading ? (
              <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full font-medium">
                <ContentLoader
                  speed={2}
                  width={40}
                  height={12}
                  backgroundColor="#f3f4f6"
                  foregroundColor="#e5e7eb"
                  style={{ display: 'inline-block', verticalAlign: 'middle' }}
                >
                  <rect x="0" y="0" rx="4" ry="4" width="40" height="12" />
                </ContentLoader>
              </span>
            ) : (
              <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full font-medium">
                {(() => {
                  const total = metricCards.patient + metricCards.psychiatrist + metricCards.nurse + metricCards.admin;
                  const percentage = total > 0 ? ((metricCards.nurse / total) * 100).toFixed(1) : 0;
                  return `${percentage}%`;
                })()}
              </span>
            )}
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">
            {loading ? (
              <ContentLoader
                speed={2}
                width={60}
                height={28}
                backgroundColor="#f3f4f6"
                foregroundColor="#e5e7eb"
                style={{ display: 'inline-block', verticalAlign: 'middle' }}
              >
                <rect x="0" y="0" rx="4" ry="4" width="60" height="28" />
              </ContentLoader>
            ) : (
              metricCards.nurse
            )}
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="space-y-6">
        <div className="py-4">
          <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between md:space-y-0 md:space-x-4">
            
            {/* Filter Tabs */}
            <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm overflow-x-auto">
              {[
                { label: 'All', count: filterCounts.all, active: activeFilter === 'All' },
                { label: 'Patients', count: metricCards.patient, active: activeFilter === 'Patients' },
                { label: 'Nurses', count: metricCards.nurse, active: activeFilter === 'Nurses' },
                { label: 'Certified Nurses', count: certiFiedNurse.length, active: activeFilter === 'Certified Nurses' },
                { label: 'Training in Progress', count: inProgessNurse.length, active: activeFilter === 'Training in Progress' },
              ].map((tab, idx) => (
                <button
                  key={idx}
                  onClick={() => handleFilterChange(tab.label)}
                  className={`text-xs md:text-sm font-medium px-3 md:px-4 py-1.5 rounded-lg transition-all duration-200 whitespace-nowrap flex-shrink-0
                    ${tab.active
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-blue-600'
                    }`}
                >
                  {tab.label} <span className="text-xs font-semibold ml-1">({tab.count})</span>
                </button>
              ))}
            </div>


            {/* Search Input with 500ms debounce */}
            <div className="relative w-full md:w-64">
              <input
                type="text"
                placeholder="Search by  email"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

          {/* Table Loading State - Only show when tableLoading is true */}
          {tableLoading ? (
            <>
              {/* Mobile Card Loading */}
              <div className="block md:hidden space-y-4">
                {[...Array(5)].map((_, index) => (
                  <MobileCardSkeleton key={index} />
                ))}
              </div>

              {/* Desktop Table Loading */}
              <div className="hidden md:block">
                <div className="space-y-1">
                  {/* Table Header */}
                  <div className="py-4">
                    <div className="grid grid-cols-12 gap-4 items-center text-xs font-medium text-gray-600 uppercase tracking-wider text-left bg-gray-100 p-4 rounded-lg">
                      <div className="col-span-3 text-left">User</div>
                      <div className="col-span-2 text-left">Role</div>
                      <div className="col-span-2 text-left">Status</div>
                      <div className="col-span-3 text-left">Last Login</div>
                      {/* <div className="col-span-2 text-left">Actions</div> */}
                    </div>
                  </div>

                  {/* Table Rows Loading */}
                  <div className="space-y-2">
                    {[...Array(5)].map((_, index) => (
                      <TableRowSkeleton key={index} />
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
          <>
              {/* No Results Message */}
              {users.length === 0 && searchTerm.trim() !== '' && (
                <div className="flex flex-col items-center justify-center h-64 text-center">
                  <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                  </svg>
                  <h2 className="text-lg font-medium text-gray-700">No users found</h2>
                  <p className="text-sm text-gray-500 mt-1">No matches for "<strong>{searchTerm}</strong>".</p>
                </div>
              )}

              {/* Mobile Card View */}
              {(showNurseTable ? nurse.length > 0 : users.length > 0) && (
                <div className="block md:hidden space-y-4">
                  {(showNurseTable ? nurse : users).map((user, index) => {
                    const userStatus = getUserStatus(user);
                    return (
                      <div 
                        key={user._id} 
                        onClick={() => user.role === 'nurse' ? handleViewNurseProfile(user) : null}
                        className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 transition-all duration-200 ${user.role === 'nurse' ? 'cursor-pointer hover:shadow-md hover:border-blue-300 hover:bg-blue-50' : ''}`}
                      >
                        <div className="flex items-start space-x-3">
                          <img
                            className="h-12 w-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                            src={getProfileImageUrl(user.profileImage) || getAvatar(user.email)}
                            alt={getUserDisplayName(user)}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-sm font-medium text-gray-900 truncate">
                                {getUserDisplayName(user)}
                              </h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userStatus.color === 'green'
                                  ? 'bg-green-100 text-green-800'
                                  : userStatus.color === 'yellow'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${userStatus.color === 'green'
                                    ? 'bg-green-500'
                                    : userStatus.color === 'yellow'
                                      ? 'bg-yellow-500'
                                      : 'bg-gray-500'
                                  }`}></div>
                                {userStatus.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2 break-words">{user.email}</p>
                            <div className="flex items-center justify-between">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(user.role)}`}>
                                {getRoleLabel(user.role)}
                              </span>
                              <div className="flex space-x-2">

                                <button className="p-2 rounded-full hover:bg-gray-100 text-gray-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path fillRule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <button className="p-2 rounded-full hover:bg-gray-100 text-gray-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                  </svg>
                                </button>
                                <button className="p-2 rounded-full hover:bg-gray-100 text-gray-400">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center">
                              <p className="text-xs text-gray-500 mt-2">{formatDateTime(user.lastLogin)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}


              {/* Desktop Table View */}
              {(showNurseTable ? nurse.length > 0 : users.length > 0) &&              
                (
                  showNurseTable?
                  (
                  <div className="hidden md:block">
                    <div className="space-y-1">
                      {/* Table Header */}
                      <div className="py-4">
                        <div
                          className="grid grid-cols-12 gap-4 items-center text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-100 p-4 rounded-lg"
                          role="row"
                        >
                          <div role="columnheader" className="col-span-4">User</div>
                          <div role="columnheader" className="col-span-2">Role</div>
                          <div role="columnheader" className="col-span-2">Status</div>
                          <div role="columnheader" className="col-span-2">Last Login</div>
                          <div role="columnheader" className="col-span-2">Training Status</div>
                        </div>
                      </div>

                      {/* Table Rows */}
                      <div className="space-y-2">
                        {(nurse || []).map((user) => {
                          const userStatus = getUserStatus(user);
                          return (
                            <div
                              key={user._id}
                              onClick={() => handleViewNurseProfile(user)}
                              className="rounded-xl border transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                            >
                              <div className="grid grid-cols-12 gap-4 px-4 py-4 items-center">
                                {/* User Info (col-span-4) */}
                                <div className="col-span-4 flex items-center">
                                  <img
                                    className="h-10 w-10 rounded-full object-cover mr-3 border-2 border-gray-200 flex-shrink-0"
                                    src={getProfileImageUrl(user.profileImage) || getAvatar(user.email)}
                                    alt={getUserDisplayName(user)}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm text-gray-900">
                                      {getUserDisplayName(user)}
                                    </div>
                                    <div className="text-xs text-gray-500 break-words">{user.email}</div>
                                  </div>
                                </div>

                                {/* Role (col-span-2) */}
                                <div className="col-span-2">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                                    {getRoleLabel(user.role)}
                                  </span>
                                </div>

                                {/* Status (col-span-2) */}
                                <div className="col-span-2">
                                  <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                      userStatus.color === 'green'
                                        ? 'bg-green-100 text-green-800'
                                        : userStatus.color === 'yellow'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    <span
                                      className={`w-1.5 h-1.5 rounded-full mr-2 ${
                                        userStatus.color === 'green'
                                          ? 'bg-green-500'
                                          : userStatus.color === 'yellow'
                                          ? 'bg-yellow-500'
                                          : 'bg-gray-500'
                                      }`}
                                    />
                                    {userStatus.status}
                                  </span>
                                </div>

                                {/* Last Login (col-span-2) */}
                                <div className="col-span-2 text-sm text-gray-600">
                                  {formatDateTime(user.lastLogin)}
                                </div>
                                
                                  {/* Training Status (col-span-2) */}           
                                  <div className="col-span-2 flex items-center justify-between">
                                    <span
                                      className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${(() => {
                                        const course = user?.courseInfo;
                                        let statusText = 'Not Started';
                                        let chipColor = 'bg-gray-100 text-gray-600';
                                        if (!course || !Array.isArray(course.progress) || course.progress.length === 0) {
                                          statusText = 'Not Started';
                                          chipColor = 'bg-gray-100 text-gray-600';
                                        } else {
                                          const total = course.progress.length;
                                          const completedCount = course.progress.filter(
                                            m => String(m.status).toLowerCase() === 'completed'
                                          ).length;

                                          if (course.isCompleted || completedCount >= total) {
                                            statusText = 'Certified';
                                            chipColor = 'bg-green-100 text-green-700';
                                          } else {
                                            const allLocked = course.progress.every(
                                              m => String(m.status).toLowerCase() === 'locked'
                                            );
                                            if (completedCount === 0 && allLocked) {
                                              statusText = 'Not Started';
                                              chipColor = 'bg-gray-100 text-gray-600';
                                            } else {
                                              const currentIndex = Math.min(completedCount + 1, total);
                                              statusText = `Module ${currentIndex}/${total}`;
                                              chipColor = 'bg-blue-100 text-blue-700';
                                            }
                                          }
                                        }
                                        return chipColor;
                                      })()}`}
                                    >
                                      {(() => {
                                        const course = user?.courseInfo;
                                        let statusText = 'Not Started';
                                        if (!course || !Array.isArray(course.progress) || course.progress.length === 0) {
                                          statusText = 'Not Started';
                                        } else {
                                          const total = course.progress.length;
                                          const completedCount = course.progress.filter(
                                            m => String(m.status).toLowerCase() === 'completed'
                                          ).length;

                                          if (course.isCompleted || completedCount >= total) {
                                            statusText = 'Certified';
                                          } else {
                                            const allLocked = course.progress.every(
                                              m => String(m.status).toLowerCase() === 'locked'
                                            );
                                            if (completedCount === 0 && allLocked) {
                                              statusText = 'Not Started';
                                            } else {
                                              const currentIndex = Math.min(completedCount + 1, total);
                                              statusText = `Module ${currentIndex}/${total}`;
                                            }
                                          }
                                        }
                                        return statusText;
                                      })()}
                                    </span>
                                    

                                  </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
):(
                  <div className="hidden md:block">
                    <div className="space-y-1">
                      {/* Table Header */}
                      <div className="py-4">
                        <div
                          className="grid grid-cols-12 gap-4 items-center text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-100 p-4 rounded-lg"
                          role="row"
                        >
                          <div role="columnheader" className="col-span-4">User</div>
                          <div role="columnheader" className="col-span-2">Role</div>
                          <div role="columnheader" className="col-span-2">Status</div>
                          <div role="columnheader" className="col-span-4">Last Login</div>
                        </div>
                      </div>

                      {/* Table Rows */}
                      <div className="space-y-2">
                        {(users || []).map((user) => {
                          const userStatus = getUserStatus(user);
                          return (
                            <div
                              key={user._id}
                              className="rounded-xl border transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50"
                            >
                              <div className="grid grid-cols-12 gap-4 px-4 py-4">
                                {/* User Info (col-span-4) */}
                                <div className="col-span-4 flex items-center">
                                  <img
                                    className="h-10 w-10 rounded-full object-cover mr-3 border-2 border-gray-200 flex-shrink-0"
                                    src={getProfileImageUrl(user.profileImage) || getAvatar(user.email)}
                                    alt={getUserDisplayName(user)}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium text-sm text-gray-900">
                                      {getUserDisplayName(user)}
                                    </div>
                                    <div className="text-xs text-gray-500 break-words">{user.email}</div>
                                  </div>
                                </div>

                                {/* Role (col-span-2) */}
                                <div className="col-span-2">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                                    {getRoleLabel(user.role)}
                                  </span>
                                </div>

                                {/* Status (col-span-2) */}
                                <div className="col-span-2">
                                  <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                      userStatus.color === 'green'
                                        ? 'bg-green-100 text-green-800'
                                        : userStatus.color === 'yellow'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    <span
                                      className={`w-1.5 h-1.5 rounded-full mr-2 ${
                                        userStatus.color === 'green'
                                          ? 'bg-green-500'
                                          : userStatus.color === 'yellow'
                                          ? 'bg-yellow-500'
                                          : 'bg-gray-500'
                                      }`}
                                    />
                                    {userStatus.status}
                                  </span>
                                </div>

                                {/* Last Login (col-span-4) */}
                                <div className="col-span-4 text-sm text-gray-600">
                                  
                                  {formatDateTime(user.lastLogin)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  )
                )
              }

              {/* Pagination */}
              {users.length > 0 && (
                <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPrevPage}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(pagination.currentPage - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(pagination.currentPage * 10, pagination.totalUsers)}</span> of{' '}
                        <span className="font-medium">{pagination.totalUsers}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={!pagination.hasPrevPage}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>

                        {/* Page numbers */}
                        {[...Array(pagination.totalPages)].map((_, i) => {
                          const pageNum = i + 1;
                          const isActive = pageNum === pagination.currentPage;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${isActive
                                  ? 'bg-blue-50 border-blue-500 text-blue-600'
                                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}

                        <button
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={!pagination.hasNextPage}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
          </>
        )}
      </div>

    {/* Nurse Profile Modal */}
{showNurseProfileModal && selectedNurse && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
         <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] min-h-[500px] flex flex-col shadow-2xl ">
      
      {/* Header - Fixed */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50 flex-shrink-0">
        <h2 className="text-xl font-semibold text-gray-900">Nurse Profile</h2>
        <button
          onClick={() => {
            setShowNurseProfileModal(false);
            setSelectedNurse(null);
            setNurseProfileData(null);
          }}
          className="text-gray-400 hover:text-gray-500"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-500 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full">
        
        {nurseProfileLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : nurseProfileData ? (
          <div className="space-y-6">

            {/* Basic Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-md font-medium text-gray-900 mb-3">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-sm text-gray-900">{nurseProfileData.nurse.name || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-sm text-gray-900">{nurseProfileData.nurse.email || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <p className="text-sm text-gray-900 capitalize">{nurseProfileData.nurse.role || "N/A"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Login</label>
                  <p className="text-sm text-gray-900">
                    {formatDateTime(nurseProfileData.nurse.lastLogin) || "Never"}
                  </p>
                </div>
               
              </div>
            </div>

            {/* Professional Information */}
            {nurseProfileData.profile && Object.keys(nurseProfileData.profile).length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-md font-medium text-gray-900 mb-3">Professional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">AHPRA Number</label>
                    <p className="text-sm text-gray-900">{nurseProfileData.profile.ahpraNumber || "N/A"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-sm text-gray-900">{nurseProfileData.profile.phone || "N/A"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <p className="text-sm text-gray-900">
                      {nurseProfileData.profile.dateOfBirth
                        ? new Date(nurseProfileData.profile.dateOfBirth).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Address</label>
                    <p className="text-sm text-gray-900">{nurseProfileData.profile.address || "N/A"}</p>
                  </div>
                  {nurseProfileData.profile.otherQualifications && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Other Qualifications</label>
                      <p className="text-sm text-gray-900">{nurseProfileData.profile.otherQualifications}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bank Details */}
            {nurseProfileData.profile?.bankDetails && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-md font-medium text-gray-900 mb-3">Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                    <p className="text-sm text-gray-900">{nurseProfileData.profile.bankDetails.bankName || "N/A"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">BSB Number</label>
                    <p className="text-sm text-gray-900">{nurseProfileData.profile.bankDetails.bsbNumber || "N/A"}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Number</label>
                    <p className="text-sm text-gray-900">{nurseProfileData.profile.bankDetails.accountNumber || "N/A"}</p>
                  </div>
                  {nurseProfileData.profile.abn && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">ABN</label>
                      <p className="text-sm text-gray-900">{nurseProfileData.profile.abn}</p>
                    </div>
                  )}
                  {nurseProfileData.profile.companyName && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Company Name</label>
                      <p className="text-sm text-gray-900">{nurseProfileData.profile.companyName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            

          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No profile data available</p>
          </div>
        )}
      </div>
      
      {/* Bottom margin for better spacing */}
      <div className="h-6"></div>
    </div>
  </div>
)}

  </div>
  );
};

export default AdminUsers;